import React from "react";
import { Button } from "@heroui/button";

import { OrderDetail } from "../../types/orders";

interface OrderActionsProps {
  order: OrderDetail;
  onUpdatePayment: (isPaid: boolean) => Promise<void>;
  loading: boolean;
}

const OrderActions: React.FC<OrderActionsProps> = ({
  order,
  onUpdatePayment,
  loading,
}) => {
  // Get actions based on order status and type
  const getAvailableActions = () => {
    const actions = [];

    // Payment actions
    if (!order.is_paid) {
      actions.push(
        <Button
          key="mark-paid"
          className="w-full"
          color="success"
          isLoading={loading}
          onPress={() => onUpdatePayment(true)}
        >
          Mark as Paid
        </Button>,
      );
    } else {
      actions.push(
        <Button
          key="mark-unpaid"
          className="w-full"
          color="danger"
          isLoading={loading}
          variant="light"
          onPress={() => onUpdatePayment(false)}
        >
          Mark as Unpaid
        </Button>,
      );
    }

    // Order type specific actions
    if (order.order_type === "delivery") {
      // If not yet assigned to a driver
      if (!order.delivery_assignment) {
        actions.push(
          <Button
            key="assign-driver"
            className="w-full"
            color="primary"
            disabled={loading}
            variant="light"
          >
            Assign Driver
          </Button>,
        );
      }
      // If already assigned but not delivered
      else if (
        order.delivery_assignment &&
        !order.delivery_assignment.delivered_at
      ) {
        actions.push(
          <Button
            key="track-delivery"
            className="w-full"
            color="primary"
            disabled={loading}
            variant="light"
          >
            Track Delivery
          </Button>,
        );
      }
    }

    // Print receipt
    actions.push(
      <Button
        key="print-receipt"
        className="w-full"
        variant="light"
        onPress={() => window.print()}
      >
        Print Receipt
      </Button>,
    );

    // Order may still be editable if it's new
    if (order.status_name.toLowerCase() === "new") {
      actions.push(
        <Button
          key="edit-order"
          className="w-full"
          disabled={loading}
          variant="light"
        >
          Edit Order
        </Button>,
      );
    }

    // Cancel order
    if (
      !["delivered", "picked_up", "cancelled"].includes(
        order.status_name.toLowerCase(),
      )
    ) {
      actions.push(
        <Button
          key="cancel-order"
          className="w-full"
          color="danger"
          disabled={loading}
          variant="light"
        >
          Cancel Order
        </Button>,
      );
    }

    // Duplicate order
    actions.push(
      <Button
        key="duplicate-order"
        className="w-full"
        disabled={loading}
        variant="light"
      >
        Duplicate Order
      </Button>,
    );

    return actions;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
          Order Actions
        </h3>
      </div>

      <div className="p-6">
        <div className="space-y-3">{getAvailableActions()}</div>
      </div>
    </div>
  );
};

export default OrderActions;
